import React, { useEffect, useState } from 'react';
import { SettingsIcon, BuildingIcon, UploadIcon, Trash2Icon, ChevronDownIcon, SaveIcon, CheckCircleIcon } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
type TabType = 'dealer' | 'design' | 'quote';
type DealerSubTabType = 'legal' | 'address' | 'contact';
export function SystemSettingsPage() {
  const {
    role,
    companyName,
    companyLogo,
    setCompanyLogo,
    officialCountry,
    setOfficialCountry
  } = useUser();
  const [activeTab, setActiveTab] = useState<TabType>('dealer');
  const [dealerSubTab, setDealerSubTab] = useState<DealerSubTabType>('legal');
  const [logoPreview, setLogoPreview] = useState<string | null>(companyLogo);
  // Legal data
  const [legalName, setLegalName] = useState('DAIRY GLOBAL EXPERTS LLC LN');
  const [taxId, setTaxId] = useState('INN 392949923233');
  const [billingCountry, setBillingCountry] = useState('');
  // Billing address
  const [lineAddress1, setLineAddress1] = useState('50 Centralna st');
  const [lineAddress2, setLineAddress2] = useState('');
  const [city, setCity] = useState('Hromy village, Uman district');
  const [stateProvince, setStateProvince] = useState('Cherkasy Region');
  const [zipCode, setZipCode] = useState('20343');
  const [distributionCountry, setDistributionCountry] = useState('Ukraine');
  // Contact
  const [phone, setPhone] = useState('+380 97 410 2423');
  const [email, setEmail] = useState('irrigation.dge@gmail.com');
  const [url, setUrl] = useState('www.dge.com.ua');
  const [dealerMarkup, setDealerMarkup] = useState('25.00');
  // Design Preferences - Irrigation Capacity
  const [applicationRate, setApplicationRate] = useState('1.30');
  const [applicationRateUnit, setApplicationRateUnit] = useState('l/s/ha');
  const [irrigationTimingMode, setIrrigationTimingMode] = useState<'hours' | 'percentage'>('hours');
  const [hoursPerDay, setHoursPerDay] = useState('18');
  const [daysPerWeek, setDaysPerWeek] = useState('7');
  const [percentageRate, setPercentageRate] = useState('75.00');
  // Design Preferences - General Configuration
  const [brand, setBrand] = useState('Pierce');
  const [model, setModel] = useState('CP600');
  const [electricalFrequency, setElectricalFrequency] = useState('60 Hz');
  const [profile, setProfile] = useState('Standard');
  // Design Preferences - Controls
  const [communicationType, setCommunicationType] = useState('GSM 4G');
  const [temperatureSensor, setTemperatureSensor] = useState(true);
  const [dosingPumpControl, setDosingPumpControl] = useState(false);
  const [stopInSlot, setStopInSlot] = useState(false);
  const [lightingArrestor, setLightingArrestor] = useState(false);
  // Design Preferences - Pivot Point
  const [pivotProfile, setPivotProfile] = useState('Standard');
  const [pivotProtectiveCoating, setPivotProtectiveCoating] = useState('Galvanized');
  const [standPipeDiameter, setStandPipeDiameter] = useState('8"5/8');
  const [heavyDuty, setHeavyDuty] = useState(false);
  // Design Preferences - Spans
  const [outletSpacing, setOutletSpacing] = useState('1.5 m');
  const [spansProtectiveCoating, setSpansProtectiveCoating] = useState('Galvanized');
  const [spansProfile, setSpansProfile] = useState('Standard');
  // Design Preferences - Drive Train
  const [motorCover, setMotorCover] = useState(true);
  const [centerDrive, setCenterDrive] = useState('UMC 35/2...');
  const [wdGearbox, setWdGearbox] = useState('Std. 52/1');
  const [tires, setTires] = useState('14.9x24');
  // Design Preferences - Electric Cables
  const [cableProtection, setCableProtection] = useState('Unshield');
  const [wireSize, setWireSize] = useState('4 mm²');
  // Design Preferences - Sprinklers
  const [maxSprinklerSpacing, setMaxSprinklerSpacing] = useState('1.50');
  const [sprinklerName, setSprinklerName] = useState('Nelson | Rotator-R3...');
  const [pad, setPad] = useState('Orange');
  const [drops, setDrops] = useState('Flex Drop with plast...');
  const [clearanceHeight, setClearanceHeight] = useState('2.60');
  const [regulator, setRegulator] = useState(true);
  const [regulatorBrand, setRegulatorBrand] = useState('Komet');
  const [nominalPressure, setNominalPressure] = useState('10 psi');
  const [verticalPosition, setVerticalPosition] = useState('Top');
  // Design Preferences - End Gun 1
  const [endGun1Brand, setEndGun1Brand] = useState('Komet');
  const [endGun1Model, setEndGun1Model] = useState('KPW HEAD-UP');
  const [endGun1Control, setEndGun1Control] = useState('Endgun Control wit...');
  // Design Preferences - End Gun 2
  const [endGun2Brand, setEndGun2Brand] = useState('');
  const [endGun2Model, setEndGun2Model] = useState('');
  const [endGun2Control, setEndGun2Control] = useState('');
  // Design Preferences - Booster Pump
  const [boosterPumpPower, setBoosterPumpPower] = useState('');
  // Save status tracking
  const [savingTab, setSavingTab] = useState<string | null>(null);
  const [savedTab, setSavedTab] = useState<string | null>(null);
  // Initialize state
  useEffect(() => {
    setLogoPreview(companyLogo);
  }, [companyLogo]);
  // Determine if user can edit logo and official country
  const canEditLogo = ['sales-manager', 'operations-manager', 'manager-business-unit', 'ceo', 'logistic', 'marketing', 'financial-manager', 'system-admin', 'dealer-admin', 'region-admin'].includes(role);
  const canEditOfficialCountry = canEditLogo;
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleRemoveLogo = () => {
    setLogoPreview(null);
  };
  const handleSaveDealerInfo = () => {
    setSavingTab('dealer');
    setCompanyLogo(logoPreview);
    setTimeout(() => {
      setSavingTab(null);
      setSavedTab('dealer');
      setTimeout(() => setSavedTab(null), 2000);
    }, 600);
  };
  const handleSaveDesignPreferences = () => {
    setSavingTab('design');
    setTimeout(() => {
      setSavingTab(null);
      setSavedTab('design');
      setTimeout(() => setSavedTab(null), 2000);
    }, 600);
  };
  const handleSaveQuote = () => {
    setSavingTab('quote');
    setTimeout(() => {
      setSavingTab(null);
      setSavedTab('quote');
      setTimeout(() => setSavedTab(null), 2000);
    }, 600);
  };
  const handleSave = () => {
    setCompanyLogo(logoPreview);
    // In a real app, we would save all the other fields here too
    alert('Settings saved successfully!');
  };
  const countries = ['México', 'Estados Unidos', 'España', 'Colombia', 'Argentina', 'Brasil', 'Chile', 'Perú', 'Alemania', 'Francia', 'Ucrania', 'Ukraine', 'Reino Unido', 'Italia', 'Canadá'];
  const tabs = [{
    id: 'dealer' as TabType,
    label: 'Dealer Information'
  }, {
    id: 'design' as TabType,
    label: 'Design Preferences'
  }, {
    id: 'quote' as TabType,
    label: 'Quote'
  }];
  const dealerSubTabs = [{
    id: 'legal' as DealerSubTabType,
    label: 'Legal Data'
  }, {
    id: 'address' as DealerSubTabType,
    label: 'Billing Address'
  }, {
    id: 'contact' as DealerSubTabType,
    label: 'Contact'
  }];
  return <div className="flex flex-col h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950" data-id="element-12701">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-12702">
        <div className="absolute top-[10%] left-[5%] w-64 h-64 bg-blue-600 rounded-full mix-blend-multiply filter blur-[80px] opacity-20" data-id="element-12703"></div>
        <div className="absolute top-[40%] right-[10%] w-72 h-72 bg-purple-600 rounded-full mix-blend-multiply filter blur-[80px] opacity-20" data-id="element-12704"></div>
        <div className="absolute bottom-[10%] left-[25%] w-80 h-80 bg-teal-600 rounded-full mix-blend-multiply filter blur-[100px] opacity-20" data-id="element-12705"></div>
      </div>

      {/* Header */}
      <div className="bg-white/5 backdrop-blur-sm border-b border-white/10 px-6 py-4 relative z-10" data-id="element-12706">
        <div className="flex justify-between items-center" data-id="element-12707">
          <div className="flex items-center" data-id="element-12708">
            <h1 className="text-xl font-bold text-white flex items-center" data-id="element-12709">
              <SettingsIcon size={24} className="mr-2 text-blue-400" data-id="element-12710" />
              System Settings
            </h1>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white/5 backdrop-blur-sm border-b border-white/10 px-6 relative z-10" data-id="element-12711">
        <div className="flex" data-id="element-12712">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-3 text-sm font-medium transition-colors relative ${activeTab === tab.id ? 'text-white' : 'text-blue-300 hover:text-white'}`} data-id="element-12713">
              {tab.label}
              {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" data-id="element-12714" />}
            </button>)}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6 relative z-10" data-id="element-12715">
        <div className="max-w-4xl mx-auto space-y-6" data-id="element-12716">
          {/* Dealer Information Tab */}
          {activeTab === 'dealer' && <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 overflow-hidden" data-id="element-12717">
              {/* Company Header Section */}
              <div className="px-6 py-4 border-b border-white/10" data-id="element-12718">
                <div className="flex items-center justify-between gap-4" data-id="element-12719">
                  {/* Left side: Logo and Company Name */}
                  <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-12720">
                    {/* Logo Section */}
                    <div className="relative flex-shrink-0" data-id="element-12721">
                      {logoPreview ? <div className="w-14 h-14 rounded-lg overflow-hidden bg-white/5 border border-white/10" data-id="element-12722">
                          <img src={logoPreview} alt="Company logo" className="w-full h-full object-cover" data-id="element-12723" />
                        </div> : <div className="w-14 h-14 rounded-lg bg-blue-600/20 flex items-center justify-center border border-white/10" data-id="element-12724">
                          <BuildingIcon size={28} className="text-blue-400" data-id="element-12725" />
                        </div>}

                      {/* Logo Upload/Remove Controls */}
                      {canEditLogo && <div className="absolute -bottom-1 -right-1 flex gap-1" data-id="element-12726">
                          <label className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white rounded-full p-1 shadow-lg border-2 border-[#0f1d3a] transition-colors" data-id="element-12727">
                            <UploadIcon size={10} data-id="element-12728" />
                            <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-id="element-12729" />
                          </label>
                          {logoPreview && <button onClick={handleRemoveLogo} className="bg-red-600 hover:bg-red-700 text-white rounded-full p-1 shadow-lg border-2 border-[#0f1d3a] transition-colors" data-id="element-12730">
                              <Trash2Icon size={10} data-id="element-12731" />
                            </button>}
                        </div>}
                    </div>

                    {/* Company Name */}
                    <div className="flex-1 min-w-0" data-id="element-12732">
                      <h3 className="text-lg font-semibold text-white truncate" data-id="element-12733">
                        {companyName}
                      </h3>
                    </div>
                  </div>

                  {/* Right side: Distribution Country */}
                  <div className="flex-shrink-0" data-id="element-12734">
                    <p className="text-xs text-blue-300 mb-1" data-id="element-12735">
                      Distribuidor oficial en
                    </p>
                    {canEditOfficialCountry ? <div className="relative" data-id="element-12736">
                        <select value={distributionCountry} onChange={e => setDistributionCountry(e.target.value)} className="appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-8 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12737">
                          {countries.map(c => <option key={c} value={c} className="bg-slate-800 text-white" data-id="element-12738">
                              {c}
                            </option>)}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-blue-300" data-id="element-12739">
                          <ChevronDownIcon size={14} data-id="element-12740" />
                        </div>
                      </div> : <p className="text-sm font-medium text-white" data-id="element-12741">
                        {distributionCountry || 'No country set'}
                      </p>}
                  </div>
                </div>
              </div>

              {/* Sub Tabs */}
              <div className="flex border-b border-white/10 px-6" data-id="element-12742">
                {dealerSubTabs.map(tab => <button key={tab.id} onClick={() => setDealerSubTab(tab.id)} className={`px-4 py-3 text-sm font-medium transition-colors relative ${dealerSubTab === tab.id ? 'text-white' : 'text-blue-300 hover:text-white'}`} data-id="element-12743">
                    {tab.label}
                    {dealerSubTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" data-id="element-12744" />}
                  </button>)}
              </div>

              {/* Sub Tab Content */}
              <div className="p-6" data-id="element-12745">
                {/* Legal Data Sub Tab */}
                {dealerSubTab === 'legal' && <div className="space-y-5" data-id="element-12746">
                    <div data-id="element-12747">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12748">
                        Legal name <span className="text-red-400" data-id="element-12749">*</span>
                      </label>
                      <input type="text" value={legalName} onChange={e => setLegalName(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Company legal name" data-id="element-12750" />
                    </div>

                    <div data-id="element-12751">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12752">
                        TAX ID / VAT ID / NIF{' '}
                        <span className="text-red-400" data-id="element-12753">*</span>
                      </label>
                      <input type="text" value={taxId} onChange={e => setTaxId(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Tax identification number" data-id="element-12754" />
                    </div>
                  </div>}

                {/* Address Sub Tab */}
                {dealerSubTab === 'address' && <div className="space-y-5" data-id="element-12755">
                    <div data-id="element-12756">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12757">
                        Address line 1 <span className="text-red-400" data-id="element-12758">*</span>
                      </label>
                      <input type="text" value={lineAddress1} onChange={e => setLineAddress1(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Street, number" data-id="element-12759" />
                    </div>

                    <div data-id="element-12760">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12761">
                        Address line 2
                      </label>
                      <input type="text" value={lineAddress2} onChange={e => setLineAddress2(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Apartment, suite, etc. (optional)" data-id="element-12762" />
                    </div>

                    <div data-id="element-12763">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12764">
                        City <span className="text-red-400" data-id="element-12765">*</span>
                      </label>
                      <input type="text" value={city} onChange={e => setCity(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="City" data-id="element-12766" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-12767">
                      <div data-id="element-12768">
                        <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12769">
                          State / Province
                        </label>
                        <input type="text" value={stateProvince} onChange={e => setStateProvince(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="State or province" data-id="element-12770" />
                      </div>

                      <div data-id="element-12771">
                        <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12772">
                          Zip code
                        </label>
                        <input type="text" value={zipCode} onChange={e => setZipCode(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Zip code" data-id="element-12773" />
                      </div>
                    </div>

                    <div data-id="element-12774">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12775">
                        Billing country <span className="text-red-400" data-id="element-12776">*</span>
                      </label>
                      <div className="relative" data-id="element-12777">
                        <select value={billingCountry} onChange={e => setBillingCountry(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12778">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-12779">
                            Please, select country
                          </option>
                          {countries.map(c => <option key={c} value={c} className="bg-slate-800 text-white" data-id="element-12780">
                              {c}
                            </option>)}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12781">
                          <ChevronDownIcon size={16} data-id="element-12782" />
                        </div>
                      </div>
                      {!billingCountry && <p className="text-red-400 text-xs mt-1" data-id="element-12783">
                          Please, select country
                        </p>}
                    </div>
                  </div>}

                {/* Contact Sub Tab */}
                {dealerSubTab === 'contact' && <div className="space-y-5" data-id="element-12784">
                    <div data-id="element-12785">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12786">
                        Phone
                      </label>
                      <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="+1 234 567 8900" data-id="element-12787" />
                    </div>

                    <div data-id="element-12788">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12789">
                        Email
                      </label>
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="company@email.com" data-id="element-12790" />
                    </div>

                    <div data-id="element-12791">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12792">
                        Website
                      </label>
                      <input type="url" value={url} onChange={e => setUrl(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="www.company.com" data-id="element-12793" />
                    </div>
                  </div>}
              </div>

              {/* Save Button for Dealer Information (common for all 3 sub-tabs) */}
              <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end bg-black/10 rounded-b-xl" data-id="element-12794">
                <button onClick={handleSaveDealerInfo} disabled={savingTab === 'dealer'} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-12795">
                  {savingTab === 'dealer' ? <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-12796" />
                      Saving...
                    </> : savedTab === 'dealer' ? <>
                      <CheckCircleIcon size={16} data-id="element-12797" />
                      Saved!
                    </> : <>
                      <SaveIcon size={16} data-id="element-12798" />
                      Save Dealer Information
                    </>}
                </button>
              </div>
            </div>}

          {/* Design Preferences Tab */}
          {activeTab === 'design' && <div className="space-y-6" data-id="element-12799">
              {/* Irrigation Capacity Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-12800">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-12801">
                  Irrigation Capacity
                </h3>
                <div className="space-y-5" data-id="element-12802">
                  <div data-id="element-12803">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12804">
                      Application rate
                    </label>
                    <div className="flex gap-3" data-id="element-12805">
                      <input type="number" step="0.01" value={applicationRate} onChange={e => setApplicationRate(e.target.value)} className="w-32 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12806" />
                      <div className="relative flex-1" data-id="element-12807">
                        <select value={applicationRateUnit} onChange={e => setApplicationRateUnit(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12808">
                          <option value="l/s/ha" className="bg-slate-800 text-white" data-id="element-12809">
                            l/s/ha
                          </option>
                          <option value="mm/h" className="bg-slate-800 text-white" data-id="element-12810">
                            mm/h
                          </option>
                          <option value="gpm/ac" className="bg-slate-800 text-white" data-id="element-12811">
                            gpm/ac
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12812">
                          <ChevronDownIcon size={16} data-id="element-12813" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div data-id="element-12814">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12815">
                      Irrigation timing rate
                    </label>
                    <div className="space-y-3" data-id="element-12816">
                      <div className="flex items-center gap-3" data-id="element-12817">
                        <input type="radio" id="timing-hours" checked={irrigationTimingMode === 'hours'} onChange={() => setIrrigationTimingMode('hours')} className="w-4 h-4 text-blue-500 accent-blue-500" data-id="element-12818" />
                        <div className="flex items-center gap-2 flex-1" data-id="element-12819">
                          <input type="number" value={hoursPerDay} onChange={e => setHoursPerDay(e.target.value)} disabled={irrigationTimingMode !== 'hours'} className="w-20 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12820" />
                          <span className="text-white text-sm" data-id="element-12821">h/day</span>
                          <input type="number" value={daysPerWeek} onChange={e => setDaysPerWeek(e.target.value)} disabled={irrigationTimingMode !== 'hours'} className="w-20 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12822" />
                          <span className="text-white text-sm" data-id="element-12823">days/week</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-3" data-id="element-12824">
                        <input type="radio" id="timing-percentage" checked={irrigationTimingMode === 'percentage'} onChange={() => setIrrigationTimingMode('percentage')} className="w-4 h-4 text-blue-500 accent-blue-500" data-id="element-12825" />
                        <div className="flex items-center gap-2 flex-1" data-id="element-12826">
                          <input type="number" step="0.01" value={percentageRate} onChange={e => setPercentageRate(e.target.value)} disabled={irrigationTimingMode !== 'percentage'} className="w-24 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12827" />
                          <span className="text-white text-sm" data-id="element-12828">% 18h/24h</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* General Configuration Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-12829">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-12830">
                  General Configuration
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-12831">
                  <div data-id="element-12832">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12833">
                      Brand
                    </label>
                    <div className="relative" data-id="element-12834">
                      <select value={brand} onChange={e => setBrand(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12835">
                        <option value="Pierce" className="bg-slate-800 text-white" data-id="element-12836">
                          Pierce
                        </option>
                        <option value="Reinke" className="bg-slate-800 text-white" data-id="element-12837">
                          Reinke
                        </option>
                        <option value="Valley" className="bg-slate-800 text-white" data-id="element-12838">
                          Valley
                        </option>
                        <option value="Zimmatic" className="bg-slate-800 text-white" data-id="element-12839">
                          Zimmatic
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12840">
                        <ChevronDownIcon size={16} data-id="element-12841" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-12842">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12843">
                      Model
                    </label>
                    <div className="relative" data-id="element-12844">
                      <select value={model} onChange={e => setModel(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12845">
                        <option value="CP600" className="bg-slate-800 text-white" data-id="element-12846">
                          CP600
                        </option>
                        <option value="CP900" className="bg-slate-800 text-white" data-id="element-12847">
                          CP900
                        </option>
                        <option value="CP1200" className="bg-slate-800 text-white" data-id="element-12848">
                          CP1200
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12849">
                        <ChevronDownIcon size={16} data-id="element-12850" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-12851">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12852">
                      Electrical Frequency
                    </label>
                    <div className="relative" data-id="element-12853">
                      <select value={electricalFrequency} onChange={e => setElectricalFrequency(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12854">
                        <option value="60 Hz" className="bg-slate-800 text-white" data-id="element-12855">
                          60 Hz
                        </option>
                        <option value="50 Hz" className="bg-slate-800 text-white" data-id="element-12856">
                          50 Hz
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12857">
                        <ChevronDownIcon size={16} data-id="element-12858" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-12859">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12860">
                      Profile
                    </label>
                    <div className="relative" data-id="element-12861">
                      <select value={profile} onChange={e => setProfile(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12862">
                        <option value="Standard" className="bg-slate-800 text-white" data-id="element-12863">
                          Standard
                        </option>
                        <option value="Heavy" className="bg-slate-800 text-white" data-id="element-12864">
                          Heavy
                        </option>
                        <option value="Extra Heavy" className="bg-slate-800 text-white" data-id="element-12865">
                          Extra Heavy
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12866">
                        <ChevronDownIcon size={16} data-id="element-12867" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Controls Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-12868">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-12869">
                  Controls
                </h3>
                <div className="space-y-5" data-id="element-12870">
                  <div data-id="element-12871">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12872">
                      Communication type
                    </label>
                    <div className="relative" data-id="element-12873">
                      <select value={communicationType} onChange={e => setCommunicationType(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12874">
                        <option value="GSM 4G" className="bg-slate-800 text-white" data-id="element-12875">
                          GSM 4G
                        </option>
                        <option value="GSM 3G" className="bg-slate-800 text-white" data-id="element-12876">
                          GSM 3G
                        </option>
                        <option value="Radio" className="bg-slate-800 text-white" data-id="element-12877">
                          Radio
                        </option>
                        <option value="WiFi" className="bg-slate-800 text-white" data-id="element-12878">
                          WiFi
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12879">
                        <ChevronDownIcon size={16} data-id="element-12880" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-12881">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-12882">
                      <input type="checkbox" checked={temperatureSensor} onChange={e => setTemperatureSensor(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-12883" />
                      <span className="text-white text-sm" data-id="element-12884">
                        Temperature Sensor
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-12885">
                      <input type="checkbox" checked={dosingPumpControl} onChange={e => setDosingPumpControl(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-12886" />
                      <span className="text-white text-sm" data-id="element-12887">
                        Dosing Pump Control
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-12888">
                      <input type="checkbox" checked={stopInSlot} onChange={e => setStopInSlot(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-12889" />
                      <span className="text-white text-sm" data-id="element-12890">
                        Stop in slot on Pivot Point
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-12891">
                      <input type="checkbox" checked={lightingArrestor} onChange={e => setLightingArrestor(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-12892" />
                      <span className="text-white text-sm" data-id="element-12893">
                        Lighting arrestor
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Pivot Point Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-12894">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-12895">
                  Pivot Point
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-12896">
                  <div data-id="element-12897">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12898">
                      Profile
                    </label>
                    <div className="relative" data-id="element-12899">
                      <select value={pivotProfile} onChange={e => setPivotProfile(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12900">
                        <option value="Standard" className="bg-slate-800 text-white" data-id="element-12901">
                          Standard
                        </option>
                        <option value="Heavy" className="bg-slate-800 text-white" data-id="element-12902">
                          Heavy
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12903">
                        <ChevronDownIcon size={16} data-id="element-12904" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-12905">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12906">
                      Protective Coating
                    </label>
                    <div className="relative" data-id="element-12907">
                      <select value={pivotProtectiveCoating} onChange={e => setPivotProtectiveCoating(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12908">
                        <option value="Galvanized" className="bg-slate-800 text-white" data-id="element-12909">
                          Galvanized
                        </option>
                        <option value="Painted" className="bg-slate-800 text-white" data-id="element-12910">
                          Painted
                        </option>
                        <option value="Stainless" className="bg-slate-800 text-white" data-id="element-12911">
                          Stainless
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12912">
                        <ChevronDownIcon size={16} data-id="element-12913" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-12914">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12915">
                      Stand Pipe Diameter
                    </label>
                    <div className="relative" data-id="element-12916">
                      <select value={standPipeDiameter} onChange={e => setStandPipeDiameter(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12917">
                        <option value='8"5/8' className="bg-slate-800 text-white" data-id="element-12918">
                          8"5/8
                        </option>
                        <option value='6"5/8' className="bg-slate-800 text-white" data-id="element-12919">
                          6"5/8
                        </option>
                        <option value="10" className="bg-slate-800 text-white" data-id="element-12920">
                          10
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12921">
                        <ChevronDownIcon size={16} data-id="element-12922" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5" data-id="element-12923">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-12924">
                    <input type="checkbox" checked={heavyDuty} onChange={e => setHeavyDuty(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-12925" />
                    <span className="text-white text-sm" data-id="element-12926">Heavy duty</span>
                  </label>
                </div>
              </div>

              {/* Spans and Drive Train - Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-12927">
                {/* Spans Section */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-12928">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-12929">
                    Spans
                  </h3>
                  <div className="space-y-5" data-id="element-12930">
                    <div data-id="element-12931">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12932">
                        Outlet Spacing
                      </label>
                      <div className="relative" data-id="element-12933">
                        <select value={outletSpacing} onChange={e => setOutletSpacing(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12934">
                          <option value="1.5 m" className="bg-slate-800 text-white" data-id="element-12935">
                            1.5 m
                          </option>
                          <option value="3 m" className="bg-slate-800 text-white" data-id="element-12936">
                            3 m
                          </option>
                          <option value="1.14 m" className="bg-slate-800 text-white" data-id="element-12937">
                            1.14 m
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12938">
                          <ChevronDownIcon size={16} data-id="element-12939" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-12940">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12941">
                        Protective Coating
                      </label>
                      <div className="relative" data-id="element-12942">
                        <select value={spansProtectiveCoating} onChange={e => setSpansProtectiveCoating(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12943">
                          <option value="Galvanized" className="bg-slate-800 text-white" data-id="element-12944">
                            Galvanized
                          </option>
                          <option value="Painted" className="bg-slate-800 text-white" data-id="element-12945">
                            Painted
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12946">
                          <ChevronDownIcon size={16} data-id="element-12947" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-12948">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12949">
                        Profile
                      </label>
                      <div className="relative" data-id="element-12950">
                        <select value={spansProfile} onChange={e => setSpansProfile(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12951">
                          <option value="Standard" className="bg-slate-800 text-white" data-id="element-12952">
                            Standard
                          </option>
                          <option value="Heavy" className="bg-slate-800 text-white" data-id="element-12953">
                            Heavy
                          </option>
                          <option value="CN+" className="bg-slate-800 text-white" data-id="element-12954">
                            CN+
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12955">
                          <ChevronDownIcon size={16} data-id="element-12956" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Drive Train Section */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-12957">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-12958">
                    Drive Train
                  </h3>
                  <div className="space-y-5" data-id="element-12959">
                    <div data-id="element-12960">
                      <label className="flex items-center gap-2 cursor-pointer mb-4" data-id="element-12961">
                        <input type="checkbox" checked={motorCover} onChange={e => setMotorCover(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-12962" />
                        <span className="text-white text-sm" data-id="element-12963">Motor cover</span>
                      </label>
                    </div>

                    <div data-id="element-12964">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12965">
                        Center Drive
                      </label>
                      <div className="relative" data-id="element-12966">
                        <select value={centerDrive} onChange={e => setCenterDrive(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12967">
                          <option value="UMC 35/2..." className="bg-slate-800 text-white" data-id="element-12968">
                            UMC 35/2...
                          </option>
                          <option value="UMC 40/2" className="bg-slate-800 text-white" data-id="element-12969">
                            UMC 40/2
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12970">
                          <ChevronDownIcon size={16} data-id="element-12971" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-12972">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12973">
                        WD Gearbox
                      </label>
                      <div className="relative" data-id="element-12974">
                        <select value={wdGearbox} onChange={e => setWdGearbox(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12975">
                          <option value="Std. 52/1" className="bg-slate-800 text-white" data-id="element-12976">
                            Std. 52/1
                          </option>
                          <option value="Std. 40/1" className="bg-slate-800 text-white" data-id="element-12977">
                            Std. 40/1
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12978">
                          <ChevronDownIcon size={16} data-id="element-12979" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-12980">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12981">
                        Tires
                      </label>
                      <div className="relative" data-id="element-12982">
                        <select value={tires} onChange={e => setTires(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12983">
                          <option value="14.9x24" className="bg-slate-800 text-white" data-id="element-12984">
                            14.9x24
                          </option>
                          <option value="11.2x24" className="bg-slate-800 text-white" data-id="element-12985">
                            11.2x24
                          </option>
                          <option value="11.2x38" className="bg-slate-800 text-white" data-id="element-12986">
                            11.2x38
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12987">
                          <ChevronDownIcon size={16} data-id="element-12988" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Electric Cables Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-12989">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-12990">
                  Electric Cables
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-12991">
                  <div data-id="element-12992">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12993">
                      Electric cable protection
                    </label>
                    <div className="relative" data-id="element-12994">
                      <select value={cableProtection} onChange={e => setCableProtection(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12995">
                        <option value="Unshield" className="bg-slate-800 text-white" data-id="element-12996">
                          Unshield
                        </option>
                        <option value="Shield" className="bg-slate-800 text-white" data-id="element-12997">
                          Shield
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12998">
                        <ChevronDownIcon size={16} data-id="element-12999" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-13000">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13001">
                      Wire size
                    </label>
                    <div className="relative" data-id="element-13002">
                      <select value={wireSize} onChange={e => setWireSize(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13003">
                        <option value="4 mm²" className="bg-slate-800 text-white" data-id="element-13004">
                          4 mm²
                        </option>
                        <option value="6 mm²" className="bg-slate-800 text-white" data-id="element-13005">
                          6 mm²
                        </option>
                        <option value="10 mm²" className="bg-slate-800 text-white" data-id="element-13006">
                          10 mm²
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13007">
                        <ChevronDownIcon size={16} data-id="element-13008" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sprinklers Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-13009">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-13010">
                  Sprinklers
                </h3>

                {/* Sprinkler Preferences Subsection */}
                <div className="mb-6" data-id="element-13011">
                  <h4 className="text-sm font-medium text-blue-200 mb-4" data-id="element-13012">
                    Sprinkler Preferences
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-13013">
                    <div data-id="element-13014">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13015">
                        Max Sprinkler Spacing
                      </label>
                      <div className="flex gap-2" data-id="element-13016">
                        <input type="number" step="0.01" value={maxSprinklerSpacing} onChange={e => setMaxSprinklerSpacing(e.target.value)} className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="1.50" data-id="element-13017" />
                        <span className="flex items-center text-blue-300 text-sm px-2" data-id="element-13018">
                          m
                        </span>
                      </div>
                    </div>

                    <div data-id="element-13019">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13020">
                        Name
                      </label>
                      <div className="relative" data-id="element-13021">
                        <select value={sprinklerName} onChange={e => setSprinklerName(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13022">
                          <option value="Nelson | Rotator-R3..." className="bg-slate-800 text-white" data-id="element-13023">
                            Nelson | Rotator-R3...
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13024">
                          <ChevronDownIcon size={16} data-id="element-13025" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13026">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13027">
                        Pad
                      </label>
                      <div className="relative" data-id="element-13028">
                        <select value={pad} onChange={e => setPad(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13029">
                          <option value="Orange" className="bg-slate-800 text-white" data-id="element-13030">
                            Orange
                          </option>
                          <option value="Blue" className="bg-slate-800 text-white" data-id="element-13031">
                            Blue
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13032">
                          <ChevronDownIcon size={16} data-id="element-13033" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13034">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13035">
                        Drops
                      </label>
                      <div className="relative" data-id="element-13036">
                        <select value={drops} onChange={e => setDrops(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13037">
                          <option value="Flex Drop with plast..." className="bg-slate-800 text-white" data-id="element-13038">
                            Flex Drop with plast...
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13039">
                          <ChevronDownIcon size={16} data-id="element-13040" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13041">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13042">
                        Clearance height
                      </label>
                      <div className="flex gap-2" data-id="element-13043">
                        <input type="number" step="0.01" value={clearanceHeight} onChange={e => setClearanceHeight(e.target.value)} className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13044" />
                        <span className="flex items-center text-blue-300 text-sm px-2" data-id="element-13045">
                          m
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-white/10 my-6" data-id="element-13046"></div>

                {/* Regulator Subsection */}
                <div data-id="element-13047">
                  <label className="flex items-center gap-2 cursor-pointer mb-4" data-id="element-13048">
                    <input type="checkbox" checked={regulator} onChange={e => setRegulator(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-13049" />
                    <h4 className="text-sm font-medium text-blue-200" data-id="element-13050">
                      Regulator
                    </h4>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-13051">
                    <div data-id="element-13052">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13053">
                        Brand
                      </label>
                      <div className="relative" data-id="element-13054">
                        <select value={regulatorBrand} onChange={e => setRegulatorBrand(e.target.value)} disabled={!regulator} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13055">
                          <option value="Komet" className="bg-slate-800 text-white" data-id="element-13056">
                            Komet
                          </option>
                          <option value="Nelson" className="bg-slate-800 text-white" data-id="element-13057">
                            Nelson
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13058">
                          <ChevronDownIcon size={16} data-id="element-13059" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13060">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13061">
                        Nominal pressure
                      </label>
                      <div className="relative" data-id="element-13062">
                        <select value={nominalPressure} onChange={e => setNominalPressure(e.target.value)} disabled={!regulator} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13063">
                          <option value="10 psi" className="bg-slate-800 text-white" data-id="element-13064">
                            10 psi
                          </option>
                          <option value="15 psi" className="bg-slate-800 text-white" data-id="element-13065">
                            15 psi
                          </option>
                          <option value="6 psi" className="bg-slate-800 text-white" data-id="element-13066">
                            6 psi
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13067">
                          <ChevronDownIcon size={16} data-id="element-13068" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13069">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13070">
                        Vertical position
                      </label>
                      <div className="relative" data-id="element-13071">
                        <select value={verticalPosition} onChange={e => setVerticalPosition(e.target.value)} disabled={!regulator} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13072">
                          <option value="Top" className="bg-slate-800 text-white" data-id="element-13073">
                            Top
                          </option>
                          <option value="Bottom" className="bg-slate-800 text-white" data-id="element-13074">
                            Bottom
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13075">
                          <ChevronDownIcon size={16} data-id="element-13076" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* End Guns and Booster Pump - Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-13077">
                {/* End Gun 1 */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-13078">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-13079">
                    End Gun 1
                  </h3>
                  <div className="space-y-5" data-id="element-13080">
                    <div data-id="element-13081">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13082">
                        Brand
                      </label>
                      <div className="relative" data-id="element-13083">
                        <select value={endGun1Brand} onChange={e => setEndGun1Brand(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13084">
                          <option value="Komet" className="bg-slate-800 text-white" data-id="element-13085">
                            Komet
                          </option>
                          <option value="Nelson" className="bg-slate-800 text-white" data-id="element-13086">
                            Nelson
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13087">
                          <ChevronDownIcon size={16} data-id="element-13088" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13089">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13090">
                        Model
                      </label>
                      <div className="relative" data-id="element-13091">
                        <select value={endGun1Model} onChange={e => setEndGun1Model(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13092">
                          <option value="KPW HEAD-UP" className="bg-slate-800 text-white" data-id="element-13093">
                            KPW HEAD-UP
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13094">
                          <ChevronDownIcon size={16} data-id="element-13095" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13096">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13097">
                        Control
                      </label>
                      <div className="relative" data-id="element-13098">
                        <select value={endGun1Control} onChange={e => setEndGun1Control(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13099">
                          <option value="Endgun Control wit..." className="bg-slate-800 text-white" data-id="element-13100">
                            Endgun Control wit...
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13101">
                          <ChevronDownIcon size={16} data-id="element-13102" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* End Gun 2 */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-13103">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-13104">
                    End Gun 2
                  </h3>
                  <div className="space-y-5" data-id="element-13105">
                    <div data-id="element-13106">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13107">
                        Brand
                      </label>
                      <div className="relative" data-id="element-13108">
                        <select value={endGun2Brand} onChange={e => setEndGun2Brand(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13109">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-13110">
                            Select brand
                          </option>
                          <option value="Komet" className="bg-slate-800 text-white" data-id="element-13111">
                            Komet
                          </option>
                          <option value="Nelson" className="bg-slate-800 text-white" data-id="element-13112">
                            Nelson
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13113">
                          <ChevronDownIcon size={16} data-id="element-13114" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13115">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13116">
                        Model
                      </label>
                      <div className="relative" data-id="element-13117">
                        <select value={endGun2Model} onChange={e => setEndGun2Model(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13118">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-13119">
                            Select model
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13120">
                          <ChevronDownIcon size={16} data-id="element-13121" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13122">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13123">
                        Control
                      </label>
                      <div className="relative" data-id="element-13124">
                        <select value={endGun2Control} onChange={e => setEndGun2Control(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13125">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-13126">
                            Select control
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13127">
                          <ChevronDownIcon size={16} data-id="element-13128" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Booster Pump */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-13129">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-13130">
                    Booster Pump
                  </h3>
                  <div className="space-y-5" data-id="element-13131">
                    <div data-id="element-13132">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13133">
                        Power
                      </label>
                      <div className="relative" data-id="element-13134">
                        <select value={boosterPumpPower} onChange={e => setBoosterPumpPower(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13135">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-13136">
                            Select power
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13137">
                          <ChevronDownIcon size={16} data-id="element-13138" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button for Design Preferences */}
              <div className="flex items-center justify-end pt-2" data-id="element-13139">
                <button onClick={handleSaveDesignPreferences} disabled={savingTab === 'design'} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-13140">
                  {savingTab === 'design' ? <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-13141" />
                      Saving...
                    </> : savedTab === 'design' ? <>
                      <CheckCircleIcon size={16} data-id="element-13142" />
                      Saved!
                    </> : <>
                      <SaveIcon size={16} data-id="element-13143" />
                      Save Design Preferences
                    </>}
                </button>
              </div>
            </div>}

          {/* Quote Tab */}
          {activeTab === 'quote' && <div className="space-y-6" data-id="element-13144">
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-13145">
                <h3 className="text-white font-semibold text-base mb-2 pb-3 border-b border-white/10" data-id="element-13146">
                  Dealer Mark-up
                </h3>
                <p className="text-blue-300/70 text-sm mb-6" data-id="element-13147">
                  This is the default margin percentage applied to all quotes
                  generated for your customers. When a new quote is created,
                  this mark-up will be automatically added on top of the base
                  price to calculate the final customer price. You can also
                  configure a different mark-up for each individual customer,
                  and you can still adjust it on individual quotes if needed.
                </p>
                <div data-id="element-13148">
                  <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13149">
                    Default mark-up <span className="text-red-400" data-id="element-13150">*</span>
                  </label>
                  <div className="relative w-48" data-id="element-13151">
                    <input type="number" step="0.01" value={dealerMarkup} onChange={e => setDealerMarkup(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 pr-10 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="25.00" data-id="element-13152" />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-blue-300" data-id="element-13153">
                      %
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button for Quote */}
              <div className="flex items-center justify-end pt-2" data-id="element-13154">
                <button onClick={handleSaveQuote} disabled={savingTab === 'quote'} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-13155">
                  {savingTab === 'quote' ? <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-13156" />
                      Saving...
                    </> : savedTab === 'quote' ? <>
                      <CheckCircleIcon size={16} data-id="element-13157" />
                      Saved!
                    </> : <>
                      <SaveIcon size={16} data-id="element-13158" />
                      Save Quote Settings
                    </>}
                </button>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}